"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import {
  Home,
  MessageCircle,
  MessageSquare,
  Users,
  Presentation,
  Hash,
  Briefcase,
  Bookmark,
  Plus,
  Search,
  ChevronRight,
  Menu,
  X,
  LogOut,
  Shield,
  Bug,
  User as UserIcon,
  type LucideIcon,
} from "lucide-react";
import { useAuthContext } from "./providers/AuthProvider";
import { useAnalytics } from "./providers/AnalyticsProvider";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useUnreadNotifications } from "@/components/providers/NotificationsProvider";
import { useAdminView } from "@/components/providers/AdminViewProvider";
import { useDmRealtime } from "@/components/providers/DmRealtimeProvider";
import { usePendingJobsCount } from "@/hooks/usePendingJobsCount";
import { Badge as DsBadge } from "@/components/ds";
import { createClient } from "@/lib/supabase/browser";
import { SHOW_MANAGE_JOBS } from "@/lib/flags";
import { countVisibleJobsSince } from "@/lib/queries/jobs";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ds";
import { resolveDisplayName } from "@/lib/displayName";
import NotificationCenter from "./NotificationCenter";
import SearchModal from "./SearchModal";
import ThemeToggle from "@/components/theme/ThemeToggle";
import MobileQuickActionSheet from "./MobileQuickActionSheet";

interface ProfileOption {
  label: string;
  href: string;
  badge?: string;
}

const railNav = [
  { name: "Home", href: "/home", icon: Home },
  { name: "Posts", href: "/posts", icon: MessageCircle },
  { name: "Messages", href: "/messages", icon: MessageSquare, badge: "messages" as const },
  { name: "Groups", href: "/groups", icon: Users },
  // Workshops — public to all logged-in users. Sits directly under Groups per
  // the design's WorkRail (easel/presentation glyph); the # Tags item stays
  // below it, unmoved.
  { name: "Workshops", href: "/workshops", icon: Presentation },
  { name: "Tags", href: "/tags", icon: Hash },
  { name: "Jobs", href: "/jobs", icon: Briefcase, badge: "jobs" as const },
];

// Manage Jobs (/jobs/manage) visibility — the "Manage Jobs" dropdown entry
// (with its applicant-count badge) and the unreviewed-applicant query below.
// Now sourced from the shared flag in @/lib/flags (imported above) so it can't
// drift from the mirror that previously lived in src/app/jobs/manage/page.tsx.

// Left-rail "new jobs" dot — a pure client-side last-seen model (localStorage),
// no DB column and unrelated to the notification system. The dot means "a job
// went public on /jobs since you last looked there," and clears on visiting
// /jobs. (Previously this dot was wrongly driven by unreviewed-applicant count.)
const JOBS_LAST_SEEN_KEY = "indiestry:jobsLastSeenAt";

function readJobsLastSeen(): string {
  if (typeof window === "undefined") return new Date().toISOString();
  let value = window.localStorage.getItem(JOBS_LAST_SEEN_KEY);
  if (!value) {
    // First-ever load: treat "now" as already-seen so a brand-new user isn't
    // dotted for every pre-existing job. The dot then only appears for jobs
    // that go live after this moment.
    value = new Date().toISOString();
    window.localStorage.setItem(JOBS_LAST_SEEN_KEY, value);
  }
  return value;
}

function markJobsSeen(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(JOBS_LAST_SEEN_KEY, new Date().toISOString());
}

export default function NavbarAuth() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isAuthenticated, signOut, loading } = useAuthContext();
  const { profile, isProfileComplete } = useUserProfile();
  const { trackEvent } = useAnalytics();
  const { isAdmin, isLoading: isAdminLoading } = useAdminView();
  // Live unread DM count — driven by the app-wide realtime subscription
  // (DmRealtimeProvider), replacing the old 30s poll of /api/dm/conversations
  // (which returned a hardcoded 0 and so never lit the badge).
  const { totalUnread: dmUnreadCount } = useDmRealtime();
  const onAdminPage = pathname?.startsWith("/admin") ?? false;
  const shouldFetchPendingJobs = Boolean(
    isAuthenticated && user && !isAdminLoading && isAdmin && onAdminPage
  );
  const { count: pendingJobsCount } = usePendingJobsCount(shouldFetchPendingJobs);

  // Single app-wide unread-notifications count (realtime). Drives the bell, the
  // rail avatar pill, and the Notifications menu badge — no local poll.
  const { unreadCount: notificationUnreadCount } = useUnreadNotifications();

  const supabase = createClient();
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  const [hasNewJobs, setHasNewJobs] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);

  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    // Only powers the (currently gated) "Manage Jobs" dropdown badge. Gated
    // behind SHOW_MANAGE_JOBS so it isn't a dead query running every 60s while
    // that surface is hidden. This is NOT the jobs-nav dot anymore — see the
    // hasNewJobs effect below.
    if (!user || !SHOW_MANAGE_JOBS) return;
    const fetchPendingApplications = async () => {
      try {
        // Count of UNREVIEWED applicants across all of the poster's own jobs —
        // i.e. applications the poster hasn't opened yet (viewed_by_poster =
        // false). This drives the "Manage Jobs" dropdown badge once that
        // surface is re-enabled (SHOW_MANAGE_JOBS). The old query counted
        // status='pending' on published jobs only, which is why the badge
        // showed a stale "13" while the page said "No jobs found".
        const { data: jobs } = await supabase
          .from("job_postings")
          .select("id")
          .eq("posted_by", user.id);
        if (!jobs || jobs.length === 0) {
          setPendingApplicationsCount(0);
          return;
        }
        const jobIds = jobs.map((j: { id: string }) => j.id);
        const { count } = await supabase
          .from("job_applications")
          .select("*", { count: "exact", head: true })
          .in("job_id", jobIds)
          // Unreviewed = not yet opened. Treat NULL the same as false so rows
          // created before the column was backfilled still count as unreviewed.
          .not("viewed_by_poster", "is", true);
        setPendingApplicationsCount(count || 0);
      } catch (error) {
        console.error("Error fetching pending applications:", error);
      }
    };
    fetchPendingApplications();
    const interval = setInterval(fetchPendingApplications, 60000);
    return () => clearInterval(interval);
  }, [user]);

  // "New jobs" dot — show when a public job went live after the stored
  // last-seen timestamp. Cheap head-only count, polled like the others.
  useEffect(() => {
    if (!user) return;
    const checkNewJobs = async () => {
      try {
        const count = await countVisibleJobsSince(supabase, readJobsLastSeen());
        setHasNewJobs(count > 0);
      } catch {
        // Non-critical nudge — never surface an error.
      }
    };
    checkNewJobs();
    const interval = setInterval(checkNewJobs, 60000);
    return () => clearInterval(interval);
  }, [user, supabase]);

  // Clear the dot the moment the user lands on the jobs board. Keyed on
  // pathname so it fires on client-side navigation (not just full reload);
  // the desktop rail, mobile drawer, and mobile tab all read the same state,
  // so this clears the dot everywhere.
  useEffect(() => {
    if (pathname === "/jobs") {
      markJobsSeen();
      setHasNewJobs(false);
    }
  }, [pathname]);

  useEffect(() => {
    setUserMenuOpen(false);
    setDrawerOpen(false);
    setQuickActionsOpen(false);
  }, [pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  const handleLogout = async () => {
    try {
      trackEvent("logout_attempted", { current_page: pathname });
      await signOut();
      router.push("/login");
    } catch (error: any) {
      console.error("Error signing out:", error);
      trackEvent("logout_failed", { error: error.message });
    }
  };

  // 'Review Jobs' moved into the Admin Panel sidebar — keeps moderation
  // tools out of the regular profile dropdown for users (and admins, who
  // now reach it via /admin/jobs from the Admin Panel header nav).
  const profileOptions: ProfileOption[] = [
    { label: "Dashboard", href: "/home" },
    {
      label: "Profile",
      // No username yet → bounce to onboarding, which is where a username
      // actually gets set; /u/me isn't a real route and would 404.
      href: profile?.username ? `/u/${profile.username}` : "/onboarding",
    },
    { label: "My Applications", href: "/profile/applications" },
    { label: "Saved Jobs", href: "/profile/saved-jobs" },
    // Manage Jobs hidden behind SHOW_MANAGE_JOBS pending redesign. Kept intact
    // (incl. the applicant-count badge) so it can be re-enabled by flipping the
    // flag; the object is conditionally spilled in rather than deleted.
    ...(SHOW_MANAGE_JOBS
      ? [
          {
            // Any logged-in user can visit; the page filters by posted_by = user.id
            // and shows an empty state if they haven't posted anything.
            label: "Manage Jobs",
            href: "/jobs/manage",
            badge:
              pendingApplicationsCount > 0
                ? String(pendingApplicationsCount > 99 ? "99+" : pendingApplicationsCount)
                : undefined,
          },
        ]
      : []),
    {
      label: "Notifications",
      href: "/notifications",
      badge:
        notificationUnreadCount > 0
          ? String(notificationUnreadCount > 99 ? "99+" : notificationUnreadCount)
          : undefined,
    },
    { label: "Settings", href: "/settings" },
  ];

  const isActive = (href: string) => {
    if (href === "/home") return pathname === "/home";
    return pathname === href || (pathname?.startsWith(href + "/") ?? false);
  };

  // "Jobs" dot = unseen public job postings (last-seen model), NOT applicant
  // counts. Clears on visiting /jobs. (Messages no longer use this dot path —
  // they render a live numeric Badge driven by dmUnreadCount.)
  const hasUnread = (badge?: "messages" | "jobs") => {
    if (badge === "jobs") return hasNewJobs;
    return false;
  };

  const breadcrumb = (() => {
    if (!pathname) return null;
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length < 2) return null;
    // Only /tags/[tag] renders a breadcrumb — the slug is human-readable context.
    // Every other route (top-level indexes AND detail pages) leaves the breadcrumb
    // area empty; the rail's active pill or the page heading conveys location.
    if (segments[0] === "tags") {
      return {
        rootLabel: "Tags",
        rootHref: "/tags",
        current: (segments[1] ?? null) as string | null,
      };
    }
    return null;
  })();

  const handleLogoutFromMenu = async () => {
    setUserMenuOpen(false);
    setDrawerOpen(false);
    await handleLogout();
  };

  const profileHref = profile?.username ? `/u/${profile.username}` : "/onboarding";
  // Use the canonical resolveDisplayName chain so the rail matches
  // what post cards and group chat show. The chain (display_name →
  // preferred_name → full_name → first+last → username → fallback)
  // never returns the email; auth.callback / profiles ensure routes
  // auto-populate `username` to the email local-part at signup, so
  // username comes near the end of the chain and full_name (which
  // typically holds the user's real / chosen name) is preferred.
  const displayName = resolveDisplayName(profile as any, "Member");

  if (loading) return null;
  if (!user) return null;

  return (
    <>
      {/* Desktop left rail */}
      <aside
        className="hidden md:flex fixed left-0 top-0 bottom-0 w-[72px] flex-col bg-surface-1 border-r border-border-subtle z-50"
        aria-label="Primary navigation"
      >
        <div className="flex items-center justify-center py-3">
          <Link href={isAuthenticated ? "/home" : "/"} aria-label="Home">
            {/* TODO: swap to commissioned small variant when delivered for sub-48px usage */}
            <Image
              src="/indiestrylogofull.png"
              alt="Indiestry"
              width={40}
              height={40}
              priority
            />
          </Link>
        </div>

        <nav className="flex flex-col items-center gap-1 px-3 mt-2">
          {railNav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const isMessages = item.badge === "messages";
            const showDot = !isMessages && hasUnread(item.badge);
            return (
              <Link
                key={item.name}
                href={item.href}
                title={item.name}
                aria-label={item.name}
                aria-current={active ? "page" : undefined}
                className="relative w-12 h-12 flex items-center justify-center group"
              >
                {active && (
                  <span
                    className="absolute -left-3 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-accent rounded-r-full"
                    aria-hidden="true"
                  />
                )}
                <span
                  className={cn(
                    "w-12 h-12 flex items-center justify-center transition-all duration-150",
                    active
                      ? "rounded-md bg-accent text-text-on-accent"
                      : "rounded-full text-text-secondary group-hover:bg-surface-2 group-hover:text-text-primary"
                  )}
                >
                  <Icon className="w-5 h-5" strokeWidth={1.75} />
                </span>
                {isMessages && dmUnreadCount > 0 && (
                  <DsBadge
                    count={dmUnreadCount}
                    tone="busy"
                    className="absolute top-0.5 right-0.5"
                  />
                )}
                {showDot && (
                  <span
                    className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-status-busy"
                    aria-label="Unread"
                  />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col items-center gap-2 pb-3">
          <div className="relative" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              className="relative focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-full"
              aria-label="Open user menu"
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <Avatar
                name={displayName}
                src={profile?.avatar_url || undefined}
                size={32}
                status="online"
              />
              <DsBadge
                count={notificationUnreadCount}
                tone="busy"
                className="absolute -top-1 -right-1"
              />
            </button>

            {userMenuOpen && (
              <div
                role="menu"
                className="absolute left-full ml-2 bottom-0 w-64 bg-surface-1 border border-border-subtle rounded-md py-2 shadow-lg z-[60]"
              >
                <div className="px-3 py-2 border-b border-border-subtle flex items-center gap-3">
                  <Avatar
                    name={displayName}
                    src={profile?.avatar_url || undefined}
                    size={36}
                    status="online"
                  />
                  <div className="min-w-0">
                    <div className="text-text-primary text-sm font-medium truncate">
                      {displayName}
                    </div>
                    <div className="text-text-secondary text-[12px] truncate">
                      @{profile?.username || "unknown"}
                    </div>
                  </div>
                </div>
                {profileOptions.map((opt) => (
                  <Link
                    key={opt.label}
                    href={opt.href}
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center justify-between px-3 py-2 text-sm text-text-primary hover:bg-surface-2 transition-colors"
                  >
                    <span>{opt.label}</span>
                    {opt.badge && (
                      <span className="bg-status-busy text-text-on-accent text-[10px] font-semibold rounded-full px-1.5 py-0.5">
                        {opt.badge}
                      </span>
                    )}
                  </Link>
                ))}
                {isAdmin && (
                  <>
                    <div className="my-1 border-t border-border-subtle" />
                    <Link
                      href="/admin"
                      role="menuitem"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-text-primary hover:bg-accent-soft hover:text-accent transition-colors"
                    >
                      <span className="inline-flex items-center gap-2">
                        <Shield className="w-4 h-4" strokeWidth={1.75} />
                        Admin Panel
                      </span>
                      <DsBadge count={pendingJobsCount} tone="busy" />
                    </Link>
                  </>
                )}
                <Link
                  href="/report-bug"
                  role="menuitem"
                  onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-text-primary hover:bg-surface-2 transition-colors"
                >
                  <Bug className="w-4 h-4" strokeWidth={1.75} />
                  Report a bug
                </Link>
                <div className="my-1 border-t border-border-subtle" />
                <button
                  type="button"
                  onClick={handleLogoutFromMenu}
                  role="menuitem"
                  className="w-full text-left px-3 py-2 text-sm text-status-busy hover:bg-surface-2 transition-colors"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Desktop top bar */}
      <header className="hidden md:flex fixed top-0 left-[72px] right-0 h-[48px] bg-surface-0 border-b border-border-subtle z-40 items-center px-4 gap-3">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          {breadcrumb && (
            <div className="flex items-center gap-1.5 text-sm text-text-secondary min-w-0">
              <Link
                href={breadcrumb.rootHref}
                className="hover:text-text-primary transition-colors"
              >
                {breadcrumb.rootLabel}
              </Link>
              {breadcrumb.current && (
                <>
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                  <span className="text-text-primary truncate">{breadcrumb.current}</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="w-full max-w-md min-w-[200px]">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 w-full h-8 px-3 rounded-sm bg-surface-2 border border-border-subtle text-text-secondary hover:text-text-primary hover:border-border-default text-sm transition-colors"
          >
            <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
            <span className="flex-1 text-left">Search posts, jobs, members</span>
            <kbd className="text-[10px] font-mono text-text-tertiary bg-surface-3 border border-border-subtle rounded px-1 py-0.5">
              ⌘K
            </kbd>
          </button>
        </div>

        {/* Theme toggle — sits between the search field and the bookmark/bell
            cluster per the prototype topbar order. Hidden unless THEME_TOGGLE
            is on; does not move the rail or search. */}
        <ThemeToggle />

        <div className="flex-1 flex items-center justify-end gap-2">
          <NotificationCenter
            userId={user?.id}
            isProfileComplete={!!isProfileComplete}
            trackEvent={trackEvent}
          />
          <Link
            href="/profile/saved-jobs"
            aria-label="Saved jobs"
            className="w-8 h-8 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
          >
            <Bookmark className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>
      </header>

      {/* Mobile top bar — height includes the notch inset (pt = env(safe-area-
          inset-top)) so the bar covers the status-area and its content row stays
          48px; the (authed) <main> reserves the same calc, so content never rides
          under it. env() is 0 on non-notched devices, so nothing changes there. */}
      <header
        className="md:hidden fixed top-0 left-0 right-0 h-[calc(48px+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-surface-1 border-b border-border-subtle z-40 flex items-center px-3 gap-2"
      >
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="w-9 h-9 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
        >
          <Menu className="w-5 h-5" strokeWidth={1.75} />
        </button>
        <Link
          href={isAuthenticated ? "/home" : "/"}
          className="flex items-center gap-2 min-w-0"
        >
          <Image
            src="/indiestrylogofull.png"
            alt="Indiestry"
            width={32}
            height={32}
            priority
          />
          <span className="text-text-primary font-semibold text-sm truncate">
            Indiestry
          </span>
        </Link>
        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            className="w-9 h-9 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
          >
            <Search className="w-5 h-5" strokeWidth={1.75} />
          </button>
          <ThemeToggle />
          <NotificationCenter
            userId={user?.id}
            isProfileComplete={!!isProfileComplete}
            trackEvent={trackEvent}
          />
        </div>
      </header>

      {/* Mobile bottom tab bar — 5 cells keep the raised [+] FAB centered
          (cell 3 of 5): Posts · Jobs · [+] · Workshops · DMs. Home and Profile
          live in the hamburger drawer. */}
      <nav
        aria-label="Mobile tabs"
        className="md:hidden fixed bottom-0 left-0 right-0 h-[64px] bg-surface-1 border-t border-border-subtle z-40 grid grid-cols-5"
      >
        <MobileTab
          href="/posts"
          icon={MessageCircle}
          label="Posts"
          active={isActive("/posts")}
        />
        <MobileTab
          href="/jobs"
          icon={Briefcase}
          label="Jobs"
          active={isActive("/jobs")}
          badge={hasNewJobs}
        />
        <button
          type="button"
          onClick={() => setQuickActionsOpen((v) => !v)}
          className="flex items-center justify-center"
          aria-label="Quick actions"
          aria-expanded={quickActionsOpen}
          aria-haspopup="dialog"
        >
          <span
            className={cn(
              "w-12 h-12 rounded-md bg-accent text-text-on-accent flex items-center justify-center transition-transform",
              quickActionsOpen ? "rotate-45" : ""
            )}
          >
            <Plus className="w-6 h-6" strokeWidth={1.75} />
          </span>
        </button>
        <MobileTab
          href="/workshops"
          icon={Presentation}
          label="Workshops"
          active={isActive("/workshops")}
        />
        <MobileTab
          href="/messages"
          icon={MessageSquare}
          label="DMs"
          active={isActive("/messages")}
          count={dmUnreadCount}
        />
      </nav>

      {/* Mobile quick-action sheet (triggered by bottom-nav + button) */}
      <MobileQuickActionSheet
        open={quickActionsOpen}
        onClose={() => setQuickActionsOpen(false)}
        profileEditHref={profileHref}
      />

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-50"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="md:hidden fixed inset-y-0 left-0 w-72 bg-surface-1 border-r border-border-subtle z-[60] flex flex-col"
            role="dialog"
            aria-label="Navigation drawer"
          >
            <div className="flex items-center justify-between p-3 border-b border-border-subtle">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar
                  name={displayName}
                  src={profile?.avatar_url || undefined}
                  size={36}
                  status="online"
                />
                <div className="min-w-0">
                  <div className="text-text-primary text-sm font-medium truncate">
                    {displayName}
                  </div>
                  <div className="text-text-secondary text-[12px] truncate">
                    @{profile?.username || "unknown"}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="w-9 h-9 flex items-center justify-center rounded-sm text-text-secondary hover:text-text-primary"
              >
                <X className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto py-2">
              {railNav.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                const isMessages = item.badge === "messages";
                const showDot = !isMessages && hasUnread(item.badge);
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    onClick={() => setDrawerOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-4 py-3 text-sm transition-colors",
                      active
                        ? "bg-surface-2 text-text-primary border-l-2 border-accent"
                        : "text-text-secondary hover:bg-surface-2 hover:text-text-primary"
                    )}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                    <span>{item.name}</span>
                    {isMessages && dmUnreadCount > 0 && (
                      <DsBadge count={dmUnreadCount} tone="busy" className="ml-auto" />
                    )}
                    {showDot && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-status-busy" />
                    )}
                  </Link>
                );
              })}
              <Link
                href={profileHref}
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
              >
                <UserIcon className="w-5 h-5" strokeWidth={1.75} />
                <span>Profile</span>
              </Link>
              <div className="my-2 border-t border-border-subtle" />
              {profileOptions.slice(1).map((opt) => (
                <Link
                  key={opt.label}
                  href={opt.href}
                  onClick={() => setDrawerOpen(false)}
                  className="flex items-center justify-between px-4 py-3 text-sm text-text-secondary hover:bg-surface-2 hover:text-text-primary transition-colors"
                >
                  <span>{opt.label}</span>
                  {opt.badge && (
                    <span className="bg-status-busy text-text-on-accent text-[10px] font-semibold rounded-full px-1.5 py-0.5">
                      {opt.badge}
                    </span>
                  )}
                </Link>
              ))}
              {isAdmin && (
                <>
                  <div className="my-2 border-t border-border-subtle" />
                  <Link
                    href="/admin"
                    onClick={() => setDrawerOpen(false)}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm text-text-primary hover:bg-accent-soft hover:text-accent transition-colors"
                  >
                    <span className="inline-flex items-center gap-3">
                      <Shield className="w-5 h-5" strokeWidth={1.75} />
                      Admin Panel
                    </span>
                    <DsBadge count={pendingJobsCount} tone="busy" />
                  </Link>
                </>
              )}
              <Link
                href="/report-bug"
                onClick={() => setDrawerOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-text-primary hover:bg-surface-2 transition-colors"
              >
                <Bug className="w-5 h-5" strokeWidth={1.75} />
                <span>Report a bug</span>
              </Link>
            </div>
            <div className="border-t border-border-subtle">
              <button
                type="button"
                onClick={handleLogoutFromMenu}
                className="flex items-center gap-3 px-4 py-3 text-sm text-status-busy hover:bg-surface-2 transition-colors w-full"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.75} />
                <span>Sign out</span>
              </button>
            </div>
          </aside>
        </>
      )}

      {searchOpen && <SearchModal onClose={() => setSearchOpen(false)} />}
    </>
  );
}

function MobileTab({
  href,
  icon: Icon,
  label,
  active,
  badge,
  count,
}: {
  href: string;
  icon: LucideIcon;
  label: string;
  active: boolean;
  badge?: boolean;
  count?: number;
}) {
  return (
    <Link
      href={href}
      className="flex flex-col items-center justify-center gap-0.5 relative"
      aria-current={active ? "page" : undefined}
    >
      <span className={cn("relative", active ? "text-accent" : "text-text-secondary")}>
        <Icon className="w-5 h-5" strokeWidth={1.75} />
        {count != null && count > 0 ? (
          <DsBadge count={count} tone="busy" className="absolute -top-1.5 -right-2.5" />
        ) : (
          badge && (
            <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-status-busy" />
          )
        )}
      </span>
      <span
        className={cn(
          "text-[10px] whitespace-nowrap",
          active ? "text-accent" : "text-text-secondary"
        )}
      >
        {label}
      </span>
      {active && (
        <span className="absolute bottom-1.5 w-6 h-0.5 bg-accent rounded-full" />
      )}
    </Link>
  );
}
